import { Popover, Transition } from '@headlessui/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsFetching } from 'react-query';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import { NavigationEndpoint } from './types';
import GenerateHighlightReelPanel from './GenerateHighlightReelPanel';
import ClipsListItemWithActions from '../ClipsListPage/ClipsListItemWithActions';
import ClipListPreviewSkeleton from '../ClipsListPage/ClipListPreviewSkeleton';
import ContentDialog from '../Sessions/uiComponents/ContentDialog/ContentDialog';
import RenameTitleModal from '../Sessions/AssetsTable/RenameTitleModal';
import ClipBulkDeleteUsageDialog from '../ClipsListPage/ClipBulkDeleteUsageDialog';
import ClipDeletionErrorDialog from '../ClipsListPage/ClipDeletionErrorDialog';
import ConfirmationDialog from '@/components/organisms/ConfirmationDialog';
import { CustomEvents } from '@/libs/eventBus/constants';
import EventBus from '@/libs/eventBus/eventBus';
import { useHighlightReelPopover } from '@/hooks/useHighlightReelPopover';
import { useReelList } from '@/hooks/useReelList';
import { useAllVisibleReels } from '@/hooks/useAllVisibleClips';
import useElementScrolledHook from '@/hooks/useElementScrolledHook';
import IconButton from '@/components/atoms/Button/IconButton';
import Button from '@/components/atoms/Button/Button';
import Icon from '@/components/atoms/Icon';
import { classnames } from '@/libs/utils';
import { core } from '@/stores/core';

const HighlightReelPopover = () => {
  const popoverRef = useRef<HTMLElement>(null);
  const [, setSearchParams] = useSearchParams();
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isHighlightReelPopoverOpen } = useHighlightReelPopover();
  const { isScrolled, handleOnScroll } = useElementScrolledHook();

  const {
    openDeleteConfirm,
    isConfirmDeleteOpen,
    closeDeleteConfirm,
    onConfirmDelete,
    reelUsageData,
    isReelUsageDialogOpen,
    closeReelUsageDialog,
    reelDeletionError,
    closeReelDeletionErrorDialog,
    setRenamingClip,
    onGenerateSocialPost,
    renamingClip,
    onSaveNewTitle,
    closeRenamingClipDialog,
    duplicateReel
  } = useReelList();

  // SessionDetail owns the fetch and writes reels into the shared clip store; the popover reads
  // the reel slice back out. The query key still tells us whether a fetch is in flight.
  const { allVisibleReels: reels } = useAllVisibleReels();
  const isFetching = useIsFetching({ queryKey: ['highlight-reels', broadcastId] });
  const isLoadingReels = !!isFetching && !reels.length;

  const [isGeneratePanelOpen, setIsGeneratePanelOpen] = useState(false);
  // With no reels to show there's nothing to fall back to, so the generate panel _is_ the empty state.
  const showGeneratePanel = isGeneratePanelOpen || (!isLoadingReels && !reels.length);

  // Closing the popover only early-returns from render — the component stays mounted, so the panel
  // flag has to be cleared explicitly or every later open lands back on the generate panel.
  useEffect(() => {
    if (!isHighlightReelPopoverOpen) {
      setIsGeneratePanelOpen(false);
    }
  }, [isHighlightReelPopoverOpen]);

  useEffect(() => {
    if (!isHighlightReelPopoverOpen) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        EventBus.dispatch(CustomEvents.HighlightListPopUp, { isOpen: false });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isHighlightReelPopoverOpen]);

  const closePopover = useCallback(() => {
    EventBus.dispatch(CustomEvents.HighlightListPopUp, { isOpen: false });
  }, []);

  const onReelSelect = useCallback(
    (clip: Clip) => {
      // Reels live at /highlights — that's what keeps the Reels nav button lit rather than Clips.
      if (!pathname.endsWith(`/${NavigationEndpoint.highlights}`)) {
        navigate(`/${eventId}/${broadcastId}/${NavigationEndpoint.highlights}`);
      }
      setSearchParams({ activeClipId: clip.id });
      if (core.getSnapshot().content?.id !== clip.content.id) {
        core.set(() => ({ content: clip.content }));
      }
      // Close over the EventBus, not this component's local dialog state: SessionDetailLayout keeps
      // its own copy of the open flag to style the nav button, and only the event reaches both.
      closePopover();
    },
    [pathname, navigate, eventId, broadcastId, setSearchParams, closePopover]
  );

  if (!isHighlightReelPopoverOpen) {
    return <></>;
  }

  return (
    <>
      <Popover
        ref={popoverRef}
        className="absolute left-20 z-[100] flex w-[28rem] flex-col rounded-2xl border"
        style={{ top: '8px', height: showGeneratePanel ? 'auto' : 'calc(100% - 8px - 128px)' }}
      >
        <div className="flex h-full overflow-hidden">
          <div className="flex size-full overflow-visible transition-all duration-[400ms] ease-in-out">
            {/* Mirror ClipsList's inner container exactly */}
            <div className="z-20 flex w-full shrink-0 flex-col items-stretch overflow-hidden rounded-2xl border !border-slate-100 bg-white shadow-xl shadow-slate-200/10">
              {showGeneratePanel ? (
                <GenerateHighlightReelPanel onCancel={closePopover} onGenerated={closePopover} />
              ) : (
                <div className="flex h-full flex-col">
                  {/* Header */}
                  <div
                    className={classnames(
                      'sticky top-0 z-20 flex h-14 w-full items-center justify-between bg-white/90 p-4 px-5 backdrop-blur transition-all duration-150 ease-in-out',
                      { 'shadow-lg shadow-slate-900/[0.075]': isScrolled }
                    )}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg font-medium">Highlight Reels</span>
                      {!isFetching && (
                        <span className="inline-flex rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-medium">
                          {reels.length}
                        </span>
                      )}
                    </div>
                    <IconButton
                      icon="IconX"
                      title="Close"
                      variation="text"
                      trackingId="highlight-reels-close-button"
                      onClick={closePopover}
                    />
                  </div>

                  {/* List body */}
                  <div className="flex grow flex-col overflow-y-auto overflow-x-hidden" onScroll={handleOnScroll}>
                    {/* Loading skeleton — mirrors ClipListPreviewSkeleton usage in ClipsList */}
                    {isLoadingReels && <ClipListPreviewSkeleton count={3} isCollapsed={false} isInPopover />}

                    {/* Reel items — same Transition + ClipsListItemWithActions as ClipsList */}
                    {!isLoadingReels &&
                      reels.map((reel: Clip) => (
                        <Transition
                          key={reel.asset_metadata.id}
                          show={!reel.isRemoved}
                          leave="transition transform duration-500 delay-100 ease-out"
                          leaveFrom="transform translate-x-0"
                          leaveTo="transform translate-x-[300px]"
                        >
                          <ClipsListItemWithActions
                            clip={reel}
                            animateClipList={false}
                            isCollapsed={false}
                            onDelete={openDeleteConfirm}
                            onRenameClip={setRenamingClip}
                            onGenerateSocialPost={onGenerateSocialPost}
                            onDuplicateClip={duplicateReel}
                            onClipSelect={onReelSelect}
                            isInPopover
                          />
                        </Transition>
                      ))}
                  </div>

                  {/* Footer — mirrors ClipsList's generate footer */}
                  <div className="border-t bg-white px-4 py-3">
                    <Button
                      variation="filled"
                      buttonSize="full"
                      trackingId="generate-highlight-reel-button"
                      onClick={() => setIsGeneratePanelOpen(true)}
                    >
                      <Icon name="IconSparkles" size={16} className="mr-2" />
                      Generate Highlight Reel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Popover>
      {renamingClip ? (
        <ContentDialog isOpen={!!renamingClip} setIsOpen={closeRenamingClipDialog} title="Rename" size="xsmall">
          <RenameTitleModal
            existingTitle={renamingClip.title}
            onSubmit={onSaveNewTitle}
            onClose={closeRenamingClipDialog}
          />
        </ContentDialog>
      ) : null}
      <ConfirmationDialog
        isOpen={isConfirmDeleteOpen}
        title="Delete Highlight Reel"
        content="Are you sure you want to delete this highlight reel? This action cannot be undone."
        confirmLabel="Delete"
        confirmTrackingId="confirm-delete-reel-button"
        cancelTrackingId="cancel-delete-reel-button"
        onClose={closeDeleteConfirm}
        onConfirm={onConfirmDelete}
      />
      <ClipBulkDeleteUsageDialog isOpen={isReelUsageDialogOpen} onClose={closeReelUsageDialog} usages={reelUsageData} />
      <ClipDeletionErrorDialog
        isOpen={!!reelDeletionError}
        onClose={closeReelDeletionErrorDialog}
        error={reelDeletionError}
      />
    </>
  );
};

export default HighlightReelPopover;
